import {
  ApolloClient, ApolloLink, InMemoryCache, createHttpLink, split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, Observable } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { getClientSideToken, clearAuthToken } from './src/utils/auth-utils';
import { getOrganizationPropertiesByHost } from './src/utils/helpers';
import { trackEvent } from './src/utils/analytics';

const isProduction = ['PROD', 'PREPROD'].includes(process.env.NEXT_PUBLIC_HOME_REMOTE_CONFIG_ENV);

export default function createApolloClient(userToken = null, hostname = 'www.nextias.com') {
  const httpLink = createHttpLink({
    uri: process.env.NEXT_PUBLIC_CORE_API_GRAPH_HOST || 'http://localhost:4000/graphql',
  });

  const logLink = new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    if (!isProduction) {
      console.log(`[Apollo] URL: ${process.env.NEXT_PUBLIC_CORE_API_GRAPH_HOST || 'http://localhost:4000/graphql'} \n`);
      console.log(`[Apollo] Operation: ${JSON.stringify(operation.operationName)}, Variables: ${JSON.stringify(operation.variables)}, Headers: ${JSON.stringify(context.headers)} \n\n`);
    }

    return forward(operation).map((response) => {
      if (!isProduction) {
        console.log(`Got response from ${operation.operationName}: ${JSON.stringify(response)} \n\n\n`);
      }

      return response;
    });
  });
  const authLink = setContext((_, { headers }) => {
    // console.log('setContext inside apollo client', headers, _);
    const organizationId = _?.variables?.orgId || '';

    // Dynamically fetch token: use provided token (SSR) or get from cookies (client-side)
    const token = userToken || (typeof window !== 'undefined' ? getClientSideToken() : null);

    // Dynamically compute organization properties on each request using current hostname
    const currentHostname = typeof window !== 'undefined' ? window.location.hostname : hostname;
    const organizationProperties = getOrganizationPropertiesByHost(currentHostname);

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        Organization: organizationProperties?.orgId || organizationId || '',
        'device-type': 4,
        'version-code': 22,
      },
    };
  });

  const errorLink = onError(({
    graphQLErrors, networkError, operation,
  }) => {
    if (networkError) {
      // eslint-disable-next-line no-console
      console.warn(`[Apollo] Network error on ${operation.operationName}:`, networkError);

      if (networkError?.statusCode === 401) {
        console.warn('Unauthorized: Token expired or invalid. Clearing cookie and continuing without user data...');
        if (typeof window !== 'undefined') {
          clearAuthToken();
        }
      }

      return Observable.of({ data: {} });
    }

    if (graphQLErrors) {
      // eslint-disable-next-line no-console
      console.warn(`[Apollo] GraphQL errors on ${operation.operationName}:`, JSON.stringify(graphQLErrors));
      // eslint-disable-next-line no-restricted-syntax
      for (const err of graphQLErrors) {
        if (err.extensions?.code === 'UNAUTHENTICATED') {
          console.warn('GraphQL unauthorized error:', err.message);
          if (typeof window !== 'undefined') {
            clearAuthToken();
          }
        }
      }

      return Observable.of({ data: {} });
    }
    return Observable.of({ data: {} });
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: process.env.NEXT_PUBLIC_CORE_API_GRAPH_HOST,
      on: {
        connected: () => {
          if (!isProduction) {
            console.log('websocket_connected', {
              type: 'websocket',
              timestamp: new Date().getTime(),
            });
          }
        },
        error: (err) => {
          console.error('websocket_error', {
            error: err?.message || 'Unknown WebSocket error',
            code: err?.code,
            type: 'websocket',
            timestamp: new Date().getTime(),
            stack: err?.stack,
          });
        // Don't throw here - let the error handling wrapper deal with it
        },
        closed: (event) => {
          if (!isProduction) {
            console.log('websocket_closed', {
              type: 'websocket',
              code: event?.code,
              reason: event?.reason,
              wasClean: event?.wasClean,
              timestamp: new Date().getTime(),
            });
          }

          // Handle specific close codes
          if (event?.code === 4001) {
            if (!isProduction) {
              console.warn('WebSocket closed due to authentication error');
            }
            // You might want to trigger a re-authentication flow here
          }
        },
      },
      retryAttempts: 5,
      retryWait: async (retries) => {
      // Exponential backoff with jitter
        const baseDelay = 1000;
        const maxDelay = 10000;
        const jitter = Math.random() * 0.3; // 30% jitter
        const delay = Math.min(baseDelay * (2 ** retries) * (1 + jitter), maxDelay);
        trackEvent('websocket_retry_attempt', {
          retries,
          delay: Math.round(delay),
          timestamp: new Date().getTime(),
        });
        if (!isProduction) {
          console.log(`WebSocket retry attempt ${retries + 1} in ${Math.round(delay)}ms`);
        }

        // eslint-disable-next-line no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, delay));
      },
      shouldRetry: (errOrCloseEvent) => {
        try {
        // Don't retry if we get authentication errors
          if (errOrCloseEvent instanceof CloseEvent) {
            const shouldRetry = errOrCloseEvent.code !== 4001;
            console.log(`WebSocket close event - shouldRetry: ${shouldRetry}, code: ${errOrCloseEvent.code}`);
            return shouldRetry;
          }

          // For other errors, check if they're retryable
          if (errOrCloseEvent instanceof Error) {
            const nonRetryableErrors = ['UNAUTHORIZED', 'FORBIDDEN', 'BAD_REQUEST'];
            const isNonRetryable = nonRetryableErrors.some((errorType) => errOrCloseEvent.message?.includes(errorType));

            if (isNonRetryable) {
              console.log('Non-retryable error detected, stopping retries');
              return false;
            }
          }

          return true;
        } catch (error) {
          console.error('Error in shouldRetry function:', error);
          return false;
        }
      },
      // connectionParams: () => {
      //   try {
      //     return {
      //       organization: organizationId,
      //       ...authObj,
      //     };
      //   } catch (error) {
      //     console.error('Error getting WebSocket connection params:', error);
      //     return {};
      //   }
      // },
      lazy: true, // Prevent immediate connection
      onNonLazyError: (errorOrCloseEvent) => {
        console.error('WebSocket non-lazy error:', {
          error: errorOrCloseEvent?.message || 'Unknown error',
          type: 'websocket_non_lazy_error',
          timestamp: new Date().getTime(),
        });
      },
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
      );
    },
    wsLink,
    authLink.concat(logLink.concat(errorLink.concat(httpLink))), // Attach auth middleware to HTTP requests
  );

  // const combinedLink = authLink.concat(logLink).concat(httpLink).concat(wsLink);
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: splitLink,
    cache: new InMemoryCache(),
    // cache: new InMemoryCache().restore(initialState || {}),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'ignore', // Global setting
      },
      query: {
        errorPolicy: 'ignore', // Global setting
      },
      mutate: {
        errorPolicy: 'ignore', // Global setting
      },
    },
  });
}
